// Color utility functions — identical in both portals
// Source: apps/*/src/util/util.tsx

export const hexToRgb = (hex: string): number[] => {
  hex = hex.replace(/^#/, '');
  const match = hex.match(/^([0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (!match) {
    throw new Error('Invalid hex color');
  }
  const rgb = parseInt(match[1], 16);
  return [(rgb >> 16) & 0xff, (rgb >> 8) & 0xff, rgb & 0xff];
};

export const getLuminance = (rgb: number[]): number => {
  const [r, g, b] = rgb.map((value) => {
    const sRGB = value / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  const luminance1 = getLuminance(rgb1);
  const luminance2 = getLuminance(rgb2);
  const [light, dark] =
    luminance1 > luminance2 ? [luminance1, luminance2] : [luminance2, luminance1];
  return (light + 0.05) / (dark + 0.05);
};

export const isContrastSufficient = (color1: string, color2: string): boolean => {
  if (!color1 || !color2) {
    return true;
  }
  try {
    const contrastRatio = getContrastRatio(color1, color2);
    return contrastRatio >= 4.5;
  } catch (error) {
    console.warn('Invalid color value:', error);
    return true;
  }
};

// ---------------------------------------------------------------------------
// Firm-branding theme tokens
//
// Firm branding (`profile.landing_color`) is stored data we do not control, so
// every value is validated before it reaches a CSS variable, and foreground
// colors are chosen by measured WCAG contrast rather than a brightness
// heuristic. A YIQ/luminance threshold picks a foreground that fails AA for
// mid-tone backgrounds — e.g. white text on #7f7f7f is only 4.00:1 where black
// would reach 5.24:1 — so both portals share the measured implementation.
// ---------------------------------------------------------------------------

export type RgbColor = { r: number; g: number; b: number; a?: number };

const PURE_DARK_FOREGROUND = '#000000';
const PURE_LIGHT_FOREGROUND = '#ffffff';

/** WCAG 2.1 AA minimum contrast ratio for normal-size text. */
export const WCAG_AA_CONTRAST_RATIO = 4.5;
/** WCAG 2.1 minimum contrast ratio for non-text UI components. */
export const WCAG_NON_TEXT_CONTRAST_RATIO = 3;
export const DEFAULT_BRANDED_ACTION_SURFACE_CONTRAST_RATIO = 1.1;

export type BrandedActionColors = {
  backgroundColor: string;
  foregroundColor: string;
};

const STRICT_HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/**
 * Values that are syntactically valid CSS colors but must not come from stored
 * profile data: `var()` would resolve against whatever the page defines, and
 * the rest are style-injection vectors.
 */
const UNSAFE_COLOR_PATTERN = /[;{}\\]|url\(|var\(|expression\(|\/\*/i;

// Expand #rgb / #rgba shorthand to #rrggbb(aa).
const expandShortHex = (hex: string): string => {
  const clean = hex.replace('#', '');
  return clean.length === 3 || clean.length === 4
    ? clean
        .split('')
        .map((channel) => channel + channel)
        .join('')
    : clean;
};

/**
 * Validates a CSS color using the browser's own parser when available, so
 * malformed values like `#12345` or `notacolor` never reach a CSS variable.
 *
 * `CSS.supports` is the primary check; jsdom does not implement it, so we fall
 * back to a style-assignment probe (an invalid value leaves `style.color`
 * empty). With no DOM at all, only strict hex is accepted.
 */
export const isValidCssColor = (color: string): boolean => {
  const trimmed = color.trim();
  if (!trimmed || UNSAFE_COLOR_PATTERN.test(trimmed)) return false;
  if (STRICT_HEX_PATTERN.test(trimmed)) return true;
  // A bare `#...` that failed the strict pattern is malformed; the DOM probe
  // agrees, but short-circuiting keeps the intent explicit.
  if (trimmed.startsWith('#')) return false;

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', trimmed);
  }

  if (typeof document === 'undefined') return false;

  const element = document.createElement('span');
  element.style.color = trimmed;
  return element.style.color !== '';
};

export const blendRgbColors = (foreground: RgbColor, background: RgbColor): RgbColor => {
  const alpha = foreground.a ?? 1;
  return {
    r: Math.round(foreground.r * alpha + background.r * (1 - alpha)),
    g: Math.round(foreground.g * alpha + background.g * (1 - alpha)),
    b: Math.round(foreground.b * alpha + background.b * (1 - alpha)),
  };
};

/** WCAG relative luminance (0–1) of a parsed color. */
export const rgbToLuminance = ({ r, g, b }: RgbColor): number => getLuminance([r, g, b]);

const parseRgbFunctionColor = (color: string): RgbColor | null => {
  const match = color.match(
    /rgba?\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)(?:,\s*(\d?(?:\.\d+)?))?/i,
  );
  if (!match) return null;
  const [, r, g, b, a] = match;
  return { r: Number(r), g: Number(g), b: Number(b), a: a ? Number(a) : 1 };
};

/**
 * Resolves any accepted CSS color to RGB. Hex is parsed directly; everything
 * else is normalized through the DOM. Returns null for values we cannot
 * resolve, so callers can fall back rather than theme from garbage.
 */
export const parseCssColorToRgb = (color: string): RgbColor | null => {
  const trimmed = color.trim();
  if (!isValidCssColor(trimmed)) return null;

  if (trimmed.startsWith('#')) {
    const expanded = expandShortHex(trimmed);
    return {
      r: parseInt(expanded.slice(0, 2), 16),
      g: parseInt(expanded.slice(2, 4), 16),
      b: parseInt(expanded.slice(4, 6), 16),
      a: expanded.length >= 8 ? parseInt(expanded.slice(6, 8), 16) / 255 : 1,
    };
  }

  if (typeof document === 'undefined') return null;

  const element = document.createElement('span');
  element.style.color = trimmed;
  document.body.appendChild(element);
  const normalizedColor = getComputedStyle(element).color;
  element.remove();

  return parseRgbFunctionColor(normalizedColor);
};

/**
 * WCAG contrast ratio (1–21) between two resolved, opaque colors. Prefer this
 * over `getContrastRatio` when you already have parsed RGB, or when the inputs
 * may be shorthand hex or functional colors that `hexToRgb` rejects.
 */
export const getRgbContrastRatio = (a: RgbColor, b: RgbColor): number => {
  const [lighter, darker] = [rgbToLuminance(a), rgbToLuminance(b)].sort((x, y) => y - x);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Flattens a possibly translucent color against the surface behind it, so
 * contrast is measured against what the user actually sees.
 */
const resolveOpaqueRgb = (color: string, baseColor?: string): RgbColor | null => {
  const parsedColor = parseCssColorToRgb(color);
  if (!parsedColor) return null;
  if ((parsedColor.a ?? 1) >= 1) return parsedColor;

  const backgroundColor = baseColor ? parseCssColorToRgb(baseColor) : undefined;
  return backgroundColor ? blendRgbColors(parsedColor, backgroundColor) : parsedColor;
};

/** Picks whichever candidate has the highest contrast against `background`. */
const bestCandidate = (background: RgbColor, candidates: string[]) =>
  candidates
    .map((candidate) => ({
      color: candidate,
      ratio: getRgbContrastRatio(parseCssColorToRgb(candidate) as RgbColor, background),
    }))
    .reduce((best, next) => (next.ratio > best.ratio ? next : best));

/** Foreground candidates a caller may substitute for its design tokens. */
export type ContrastForegrounds = { dark?: string; light?: string };

/**
 * Returns the foreground color with the best measured contrast against
 * `bgColor`, composited over `baseColor` when `bgColor` is translucent.
 *
 * Defaults to pure black/white. Pass `foregrounds` to use design tokens
 * instead; a themed dark token lighter than pure black cannot reach AA on
 * mid-tone backgrounds, so in that band the dark candidate escalates to
 * `#000000` rather than returning a color that fails.
 *
 * Falls back to the light foreground when the background cannot be resolved.
 */
export const getContrastColor = (
  bgColor: string,
  baseColor?: string,
  foregrounds: ContrastForegrounds = {},
): string => {
  const dark = foregrounds.dark ?? PURE_DARK_FOREGROUND;
  const light = foregrounds.light ?? PURE_LIGHT_FOREGROUND;

  const background = resolveOpaqueRgb(bgColor, baseColor);
  if (!background) return light;

  const themed = bestCandidate(background, [dark, light]);
  if (themed.ratio >= WCAG_AA_CONTRAST_RATIO) return themed.color;

  const escalated = bestCandidate(background, [PURE_DARK_FOREGROUND, light]);
  return escalated.ratio > themed.ratio ? escalated.color : themed.color;
};

/**
 * Flattens `bgColor` over `baseColor` into an opaque `rgb()` string, so a
 * translucent surface can be passed on as a concrete background. Returns
 * `bgColor` unchanged when it cannot be resolved.
 */
export const getEffectiveBackgroundColor = (bgColor: string, baseColor: string): string => {
  const color = resolveOpaqueRgb(bgColor, baseColor);
  if (!color) return bgColor;
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const clampRgbChannel = (channel: number): number => Math.round(clamp(channel, 0, 255));

const normalizeRgbColor = ({ r, g, b, a }: RgbColor): RgbColor => ({
  r: clampRgbChannel(r),
  g: clampRgbChannel(g),
  b: clampRgbChannel(b),
  ...(a === undefined ? {} : { a }),
});

const rgbToCssColor = (color: RgbColor): string => {
  const { r, g, b } = normalizeRgbColor(color);
  return `rgb(${r}, ${g}, ${b})`;
};

const rgbChannelToUnit = (channel: number): number => clamp(channel, 0, 255) / 255;

type OklabColor = { l: number; a: number; b: number };
type OklchColor = { l: number; c: number; h: number };

const INTENTIONAL_TINT_MIN_OKLCH_CHROMA = 0.12;

const getHslSaturation = ({ r, g, b }: RgbColor): number => {
  const red = rgbChannelToUnit(r);
  const green = rgbChannelToUnit(g);
  const blue = rgbChannelToUnit(b);
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);

  if (max === min) {
    return 0;
  }

  const lightness = (max + min) / 2;
  const delta = max - min;
  return lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
};

const srgbChannelToLinear = (channel: number): number => {
  const value = rgbChannelToUnit(channel);
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
};

const linearChannelToSrgb = (channel: number): number => {
  const value = channel <= 0.0031308 ? 12.92 * channel : 1.055 * channel ** (1 / 2.4) - 0.055;
  return value * 255;
};

const rgbToOklab = ({ r, g, b }: RgbColor): OklabColor => {
  const red = srgbChannelToLinear(r);
  const green = srgbChannelToLinear(g);
  const blue = srgbChannelToLinear(b);

  const long = Math.cbrt(0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue);
  const medium = Math.cbrt(0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue);
  const short = Math.cbrt(0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue);

  return {
    l: 0.2104542553 * long + 0.793617785 * medium - 0.0040720468 * short,
    a: 1.9779984951 * long - 2.428592205 * medium + 0.4505937099 * short,
    b: 0.0259040371 * long + 0.7827717662 * medium - 0.808675766 * short,
  };
};

const oklabToRgb = ({ l, a, b }: OklabColor): RgbColor => {
  const long = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const medium = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const short = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;

  return {
    r: linearChannelToSrgb(4.0767416621 * long - 3.3077115913 * medium + 0.2309699292 * short),
    g: linearChannelToSrgb(-1.2684380046 * long + 2.6097574011 * medium - 0.3413193965 * short),
    b: linearChannelToSrgb(-0.0041960863 * long - 0.7034186147 * medium + 1.707614701 * short),
  };
};

const rgbToOklch = (color: RgbColor): OklchColor => {
  const lab = rgbToOklab(color);
  return {
    l: lab.l,
    c: Math.hypot(lab.a, lab.b),
    h: Math.atan2(lab.b, lab.a),
  };
};

const oklchToOklab = ({ l, c, h }: OklchColor): OklabColor => ({
  l,
  a: Math.cos(h) * c,
  b: Math.sin(h) * c,
});

const isInRgbGamut = ({ r, g, b }: RgbColor): boolean =>
  r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255;

const oklchToRgb = (color: OklchColor): RgbColor => {
  const candidate = oklabToRgb(oklchToOklab(color));
  if (isInRgbGamut(candidate)) {
    return candidate;
  }

  let lowChroma = 0;
  let highChroma = color.c;
  for (let index = 0; index < 24; index += 1) {
    const midChroma = (lowChroma + highChroma) / 2;
    const midCandidate = oklabToRgb(oklchToOklab({ ...color, c: midChroma }));
    if (isInRgbGamut(midCandidate)) {
      lowChroma = midChroma;
    } else {
      highChroma = midChroma;
    }
  }

  return oklabToRgb(oklchToOklab({ ...color, c: lowChroma }));
};

const resolveVisibleColor = (color: string, surfaceColor: string): RgbColor | null =>
  parseCssColorToRgb(getEffectiveBackgroundColor(color, surfaceColor));

const moveColorPerceptuallyToward = (
  color: RgbColor,
  target: RgbColor,
  amount: number,
): RgbColor => {
  const colorOklch = rgbToOklch(color);
  const targetOklch = rgbToOklch(target);
  const intendedChroma = Math.max(
    colorOklch.c,
    INTENTIONAL_TINT_MIN_OKLCH_CHROMA * getHslSaturation(color),
  );

  return oklchToRgb({
    ...colorOklch,
    c: intendedChroma,
    l: colorOklch.l + (targetOklch.l - colorOklch.l) * amount,
  });
};

const findNearestPassingBrandColor = ({
  color,
  target,
  passes,
}: {
  color: RgbColor;
  target: RgbColor;
  passes: (candidate: RgbColor) => boolean;
}): RgbColor | null => {
  let lowAmount = 0;
  let highAmount = 1;
  let best: RgbColor | null = null;

  for (let index = 0; index < 24; index += 1) {
    const midAmount = (lowAmount + highAmount) / 2;
    const candidate = moveColorPerceptuallyToward(color, target, midAmount);
    if (passes(candidate)) {
      best = candidate;
      highAmount = midAmount;
    } else {
      lowAmount = midAmount;
    }
  }

  return best;
};

const getContrastTargetForSurface = (surface: RgbColor): RgbColor | null => {
  const black = parseCssColorToRgb(PURE_DARK_FOREGROUND);
  const white = parseCssColorToRgb(PURE_LIGHT_FOREGROUND);
  if (!black || !white) {
    return null;
  }
  return getRgbContrastRatio(black, surface) >= getRgbContrastRatio(white, surface) ? black : white;
};

export const getVisibleBrandActionColor = ({
  primaryColor,
  surfaceColor,
  pageBackgroundColor,
  minimumContrastRatio,
}: {
  primaryColor: string;
  surfaceColor: string;
  pageBackgroundColor: string;
  minimumContrastRatio: number;
}): string => {
  const effectiveSurfaceColor = getEffectiveBackgroundColor(surfaceColor, pageBackgroundColor);
  const surface = parseCssColorToRgb(effectiveSurfaceColor);
  const visiblePrimary = resolveVisibleColor(primaryColor, effectiveSurfaceColor);

  if (!surface || !visiblePrimary) {
    return primaryColor;
  }

  if (getRgbContrastRatio(visiblePrimary, surface) >= minimumContrastRatio) {
    return primaryColor;
  }

  const target = getContrastTargetForSurface(surface);
  if (!target) {
    return primaryColor;
  }

  const candidate = findNearestPassingBrandColor({
    color: visiblePrimary,
    target,
    passes: (next) => getRgbContrastRatio(normalizeRgbColor(next), surface) >= minimumContrastRatio,
  });

  return candidate ? rgbToCssColor(candidate) : rgbToCssColor(target);
};

export const getBrandedActionColors = ({
  primaryColor,
  surfaceColor,
  pageBackgroundColor,
  minimumSurfaceContrastRatio = DEFAULT_BRANDED_ACTION_SURFACE_CONTRAST_RATIO,
  foregrounds,
}: {
  primaryColor: string;
  surfaceColor: string;
  pageBackgroundColor: string;
  minimumSurfaceContrastRatio?: number;
  foregrounds?: ContrastForegrounds;
}): BrandedActionColors => {
  const effectiveSurfaceColor = getEffectiveBackgroundColor(surfaceColor, pageBackgroundColor);
  const surface = parseCssColorToRgb(effectiveSurfaceColor);
  const visiblePrimary = resolveVisibleColor(primaryColor, effectiveSurfaceColor);

  const buildPair = (backgroundColor: string): BrandedActionColors => ({
    backgroundColor,
    foregroundColor: getContrastColor(backgroundColor, effectiveSurfaceColor, foregrounds),
  });

  const isReadablePair = (backgroundColor: string, background: RgbColor): boolean => {
    if (!surface) {
      return true;
    }
    if (getRgbContrastRatio(normalizeRgbColor(background), surface) < minimumSurfaceContrastRatio) {
      return false;
    }
    const foreground = parseCssColorToRgb(buildPair(backgroundColor).foregroundColor);
    const resolvedBackground = resolveVisibleColor(backgroundColor, effectiveSurfaceColor);
    return Boolean(
      foreground &&
      resolvedBackground &&
      getRgbContrastRatio(foreground, resolvedBackground) >= WCAG_AA_CONTRAST_RATIO,
    );
  };

  if (!surface || !visiblePrimary) {
    return buildPair(primaryColor);
  }

  if (isReadablePair(primaryColor, visiblePrimary)) {
    return buildPair(primaryColor);
  }

  const target = getContrastTargetForSurface(surface);
  if (!target) {
    return buildPair(primaryColor);
  }

  const candidate = findNearestPassingBrandColor({
    color: visiblePrimary,
    target,
    passes: (next) => {
      const renderedBackground = normalizeRgbColor(next);
      const backgroundColor = rgbToCssColor(renderedBackground);
      return isReadablePair(backgroundColor, renderedBackground);
    },
  });

  if (candidate) {
    return buildPair(rgbToCssColor(candidate));
  }

  return buildPair(rgbToCssColor(target));
};
